import { useEffect, useRef, useCallback } from 'react';
import PlayerWS from './PlayerWS';
import MessageQueue from './MessageQueue';

export const usePlayerWs = (wsUrl, options = {}) => {
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const messageQueueRef = useRef(null);

  // Event handlers and options
  const { onObjectDetection, onConnectionChange, onError, onMetadataUpdate, onFrame, onSettled, onServerStop, profile } = options;

  // Initialize message queue if object detection is needed
  useEffect(() => {
    if (onObjectDetection && !messageQueueRef.current) {
      messageQueueRef.current = new MessageQueue();

      // Subscribe the raw handler so the cleanup below unsubscribes the SAME reference.
      // (Previously subscribed a local wrapper but unsubscribed onObjectDetection, so cleanup
      // was a silent no-op.)
      messageQueueRef.current.subscribe('objectEvent', onObjectDetection);
    }

    return () => {
      if (messageQueueRef.current && onObjectDetection) {
        messageQueueRef.current.unsubscribe('objectEvent', onObjectDetection);
      }
    };
  }, [onObjectDetection]);

  // Initialize player when canvas ref is available
  useEffect(() => {
    // Don't initialize if wsUrl is null (disabled camera)
    if (!wsUrl || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;

    // Always clean up old player before creating new one
    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current = null;
    }

    // Initial canvas size - PlayerWS will resize to match video resolution
    canvas.width = 640;
    canvas.height = 360;

    // Store reference for cleanup
    playerRef.current = new PlayerWS(
      canvas,
      wsUrl,
      messageQueueRef.current,
      {
        onConnectionChange,
        onError,
        onMetadataUpdate,
        onFrame,
        onSettled,
        onServerStop,
        profile
      }
    );

    return () => {
      if (playerRef.current) {
        playerRef.current.stop();
        playerRef.current = null;
      }
    };

  // eslint-disable-next-line
  }, [wsUrl]); // Recreate whenever wsUrl changes

  const reconnect = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.connect(true);
    }
  }, []);

  const stop = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.stop();
    }
  }, []);

  // Stable accessor for the Camera Metadata overlay (#86bb5dx62): reads the CURRENT player
  // (playerRef survives re-renders + wsUrl swaps), so a 1s poll always hits the live decoder.
  const getStats = useCallback(() => (playerRef.current ? playerRef.current.getStats() : null), []);

  return {
    canvasRef,
    reconnect,
    stop,
    getStats,
    player: playerRef.current
  };
};
